import React, { useEffect, useState } from 'react';
import {
    PoseLogV1Schema,
    PoseLogV2Schema,
    poseLogIsV1,
    getV1PoseLogDimensions,
    convertV1PoseLog,
    poseLogIsV2,
    getV2PoseLogDimensions,
    convertV2PoseLog,
    getPhasesFromLog,
    PoseLogPhase,
    ExerPoseBodyAccessor,
    MediaPipePoseBodyAccessor,
    ExecutableAssessment,
    UnifiedKeypoints,
} from '@exerai/vision-engine-web';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { Maybe, Nullish, ScanSessionResultsInsights } from '@/common/types';
import { SessionReplay } from '@/components/Scan/Replay/SessionReplay';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { useAssessmentsContext } from '@/components/context/hooks/AssessmentsContext';
import { resultIsError, Result } from '@/services/HttpService';

interface SessionReplayProps {
    sessionUUID: string;
    client: 'gait' | 'scan';
    assessmentId: string;
    resultsInsights?: Maybe<ScanSessionResultsInsights>;
}

export const SessionReplayContainer = (props: SessionReplayProps) => {
    const { sessionUUID, client, assessmentId, resultsInsights } = props;
    const { httpService } = useHttpContext();
    const { assessmentsDict } = useAssessmentsContext();
    const [poseDimensions, setPoseDimensions] = useState<{ width: number; height: number }>({
        width: 900,
        height: 640,
    });
    const [poseLog, setPoseLog] = useState<Nullish<UnifiedKeypoints[]>>();
    const [phasesFromLog, setPhasesFromLog] = useState<PoseLogPhase[]>();
    const [assessmentDefinition, setAssessmentDefinition] = useState<ExecutableAssessment>();
    const [errorString, setErrorString] = useState<string>();

    useEffect(() => {
        (async () => {
            const res = await httpService.adminGetSessionPoseLogSignedUrl(client, sessionUUID);
            if (resultIsError(res)) {
                setErrorString(res.message);
                return;
            }
            const poseRes: Result<PoseLogV1Schema | PoseLogV2Schema> = await httpService.getExternalUrl(res);
            if (resultIsError(poseRes)) {
                setErrorString(poseRes.message);
                return;
            }
            let convertedPoseLog: UnifiedKeypoints[];
            let bodyAccessor = ExerPoseBodyAccessor;
            if ('meta' in poseRes) {
                if (poseRes.meta?.poseModel?.name === 'mediapipe-landmark') {
                    bodyAccessor = MediaPipePoseBodyAccessor;
                }
            }
            if (poseLogIsV1(poseRes)) {
                const dimensions = getV1PoseLogDimensions(poseRes);
                if (dimensions) {
                    setPoseDimensions(dimensions);
                }
                convertedPoseLog = convertV1PoseLog(poseRes, bodyAccessor);
                setPoseLog(convertedPoseLog);
            } else if (poseLogIsV2(poseRes)) {
                const dimensions = getV2PoseLogDimensions(poseRes);
                if (dimensions) {
                    setPoseDimensions(dimensions);
                }
                convertedPoseLog = convertV2PoseLog(poseRes, bodyAccessor);
                if (poseRes.meta.scanVersion) {
                    setPoseLog(convertedPoseLog);
                } else {
                    setErrorString('Cannot replay for sessions < 2.18.0');
                }
                setPhasesFromLog(getPhasesFromLog(poseRes.meta));
            }
            assessmentsDict &&
                assessmentsDict[assessmentId].directives &&
                setAssessmentDefinition(assessmentsDict[assessmentId] as ExecutableAssessment);
        })();
    }, [assessmentId, assessmentsDict, client, httpService, sessionUUID]);

    return poseLog && assessmentDefinition ? (
        <SessionReplay
            poseDimensions={poseDimensions}
            poseLog={poseLog}
            phasesFromLog={phasesFromLog}
            assessmentDefinition={assessmentDefinition}
            resultsInsights={resultsInsights}
        />
    ) : errorString ? (
        <div>{errorString}</div>
    ) : (
        <LoadingSpinner />
    );
};
